<!--
  The link component, supporting different styles.
  NOTE: unlike other components, XLink doesn't provide a "size" option. The reason is we normally want to inherit the text size from the parent.
-->
<script setup lang="ts">import { computed as _computed } from 'vue';

import InertiaLink from '@corp/components/InertiaLink'
import { isSameApp } from '@corp/helpers/url'
import type { ButtonHTMLAttributes } from 'vue'
import type { IconDefinition } from './XIcon.vue'

withDefaults(defineProps<{
  // Style variants for the link.
  primary?: boolean
  secondary?: boolean
  danger?: boolean

  // Does not respond to click events when disabled.
  disabled?: boolean

  // Causes the browser to treat the linked URL as a download.
  download?: boolean | string

  // Where to display the linked URL
  target?: '_blank'

  // The location this link should navigate to.
  href?: string

  // The name or font awesome path of an icon to render inside the button.
  icon?: false | IconDefinition

  // In case it is a button, the type of button, which affects the behavior when inside forms, etc.
  type?: ButtonHTMLAttributes['type']

  // Classes to pass to icon
  iconClass?: string
}>(), { type: 'button', })

const tag = _computed(() => {
  if (!__props.href) return 'button'
  if (__props.download || __props.target || !isSameApp(__props.href) || String(__props.href).startsWith('mailto:')) return 'a'
  return InertiaLink
})

const attrs = _computed(() => {
  if (tag.value === 'button') return { disabled: __props.disabled, type: __props.type }

  return {
    download: __props.download === true ? '' : __props.download === false ? undefined : '',
    href: __props.href,
    target: __props.target,
    class: { 'pointer-events-none': __props.disabled },
  }
})

const variant = _computed(() => {
  if (__props.primary || __props.secondary || __props.danger || __props.icon)
    return ['link font-semibold', { primary: __props.primary, secondary: __props.secondary, danger: __props.danger, 'with-icon': __props.icon }]
})
</script>

<template>
  <component :is="tag" :class="variant" v-bind="attrs">
    <XIcon
      v-if="icon"
      size="inherit"
      :icon="icon"
      class="mr-2 color-dark-500"
      :class="iconClass"
    />
    <slot/>
  </component>
</template>

<style lang="scss" scoped>
button[disabled] {
  opacity: 0.2;
}

.icon {
  vertical-align: -0.15em;
}

.link {
  transition: color ease 0.3s;

  &.with-icon {
    height: 1em; // This is necessary for Icon to inherit size
  }

  &:focus {
    outline: none;
  }

  &.primary {
    color: theme("colors.blue.400");

    &:hover,
    &:focus {
      color: theme("colors.blue.300");
    }

    &:active {
      color: theme("colors.blue.500");
    }
  }

  &.secondary {
    color: theme("colors.dark.500");

    &:hover,
    &:focus {
      color: theme("colors.dark.300");
    }

    &:active {
      color: theme("colors.dark.500");
    }
  }

  &.danger {
    color: theme("colors.red.400");

    &:hover,
    &:focus {
      color: theme("colors.red.300");
    }

    &:active {
      color: theme("colors.red.500");
    }
  }
}
</style>
